/* ═══════════════════════════════════════════════════════════════
   Project tokens widget (SYNC_PLAN §3.5 / D10; Project Canvas R5) — the
   project's own named tokens ({ProjectToken:<id>}) as a stock widget.
   Body is a read-only list (name → content); the header pencil opens a
   "Manage tokens" Sheet whose body IS ProjectFormaTokens, saved through
   the SAME path the Manage project Sheet uses (`ctx.saveProject`).
   Permissions: client gate `ctx.can.edit` (projects.edit, ANDed with
   !locked); server = the generic projects write check (projects.edit).
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { WidgetFrame, WidgetActionButton, WList } from '../../../components/widgets';
import type { WListRow } from '../../../components/widgets';
import EmptyState from '../../../components/EmptyState';
import { Button } from '../../../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import {
  ProjectFormaTokens,
  resolveProjectTokenContent,
  type ProjectFormaToken,
} from '../../../components/ProjectFormaTokens';
import { sectionLabel } from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

function ProjectTokensCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const p = ctx.project;
  const saved = (((p as any).formaTokens as ProjectFormaToken[] | undefined) || []).filter(Boolean);
  const proj = p as any;
  const clientName = ctx.orgs.find((o) => o.id === p.clientOrgId)?.name || p.clientName || '';
  const meta: Record<string, string> = {
    'Project.Name': p.name || '',
    'Project.ClientProjectName': proj.clientProjectName || p.name || '',
    'Project.Number': proj.projectNumber || '',
    'Project.Address': proj.address || '',
    'Client.Name': clientName,
  };
  const builtIn: WListRow[] = [
    { id: 'b-name', primary: 'Project name', meta: meta['Project.Name'] || '—' },
    {
      id: 'b-cpn',
      primary: 'Client project name',
      meta: proj.clientProjectName || '—',
    },
    { id: 'b-num', primary: 'Project number', meta: meta['Project.Number'] || '—' },
    { id: 'b-addr', primary: 'Address', meta: meta['Project.Address'] || '—' },
    { id: 'b-client', primary: 'Client', meta: clientName || '—' },
    { id: 'b-iname', primary: 'Issuance name', meta: 'Set when the project is issued' },
    { id: 'b-idate', primary: 'Issuance date', meta: 'Set when the project is issued' },
    { id: 'b-deliv', primary: 'Deliverable', meta: 'Picked at export' },
  ];
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ProjectFormaToken[]>(saved);
  const [saving, setSaving] = useState(false);

  // Re-seed from the project each time the Sheet opens.
  useEffect(() => {
    if (open) setDraft(JSON.parse(JSON.stringify(saved)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await ctx.saveProject({ ...p, formaTokens: draft } as typeof p);
      ctx.toast('Tokens saved');
      setOpen(false);
    } catch (err: any) {
      ctx.toast(err?.message || 'Something went wrong', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <WidgetFrame
        title="Project tokens"
        status="ready"
        form="list"
        actions={
          ctx.can.edit ? (
            <WidgetActionButton icon={Pencil} label="Manage tokens" onClick={() => setOpen(true)} />
          ) : undefined
        }
      >
        <div style={sectionLabel}>From the project</div>
        <WList rows={builtIn} max={builtIn.length} />
        <div style={{ ...sectionLabel, marginTop: 'var(--space-4)' }}>Your tokens</div>
        {saved.length === 0 ? (
          <EmptyState
            compact
            title="No custom tokens yet"
            action={ctx.can.edit ? { label: 'Add token', onClick: () => setOpen(true) } : undefined}
          />
        ) : (
          <WList
            rows={saved.map(
              (t): WListRow => ({
                id: t.id,
                primary: t.name || '(unnamed)',
                meta: resolveProjectTokenContent(t.content || '', meta) || '—',
              })
            )}
            max={Math.max(saved.length, 1)}
          />
        )}
        <div
          style={{
            marginTop: 'var(--space-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
          }}
        >
          Place any of these from the Tokens palette in a forma's header or footer.
        </div>
      </WidgetFrame>

      <Sheet open={open} onOpenChange={(o) => !o && !saving && setOpen(false)}>
        <SheetContent side="right" className="!w-[clamp(26rem,40vw,44rem)]">
          <SheetHeader>
            <SheetTitle>Manage tokens</SheetTitle>
          </SheetHeader>
          <div style={{ marginTop: 'var(--space-4)', flex: 1, overflowY: 'auto' }}>
            <ProjectFormaTokens tokens={draft} onChange={setDraft} />
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

export const projectTokensWidget: ProjectWidgetDef = {
  id: 'project_tokens',
  title: 'Project tokens',
  description: "The project's own named tokens, placed in its formas as {ProjectToken:…}.",
  category: 'overview',
  cols: { min: 6, max: 12 },
  defaultSize: 'standard',
  inputSurface: true,
  render: (ctx) => <ProjectTokensCard ctx={ctx} />,
};
